'use client'

import { SignType } from '@/features/Auth/auth.types'
import type { AppWalletAdapter } from '@/shared/wallet/wallet.types'
import { WalletName } from '@solana/wallet-adapter-base'
import { useWallet } from '@solana/wallet-adapter-react'
import { useCallback, useEffect, useMemo } from 'react'

// Tracks an explicit, user-initiated disconnect for this browser tab session (module-level, not
// persisted - resets on a full page reload). Every component that calls this hook runs its own
// copy of the pre-select effect below, so navigating between pages after disconnecting re-mounts
// it elsewhere; without this flag that re-mount would immediately re-select the wallet and the
// wallet-adapter library would silently auto-connect it again, undoing the disconnect.
let manuallyDisconnected = false

// Test-only: reset the module-level disconnect flag between test cases.
export function __resetManualDisconnectFlagForTests() {
  manuallyDisconnected = false
}

export function useSolanaAdapterWallet(walletType: string): AppWalletAdapter {
  const {
    wallet,
    publicKey,
    connected,
    connecting,
    select,
    connect: adapterConnect,
    disconnect: adapterDisconnect,
    signMessage,
    signTransaction,
  } = useWallet()

  useEffect(() => {
    if (!walletType || manuallyDisconnected) return
    select(walletType as WalletName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletType])

  const connect = useCallback(async () => {
    try {
      manuallyDisconnected = false
      select(walletType as WalletName)
      await new Promise((resolve) => setTimeout(resolve, 0))
      await adapterConnect()
    } catch (e) {
      // WalletNotSelectedError, WalletWindowClosedError, etc. - user may have dismissed extension
    }
  }, [adapterConnect, select, walletType])

  const disconnect = useCallback(async () => {
    manuallyDisconnected = true
    await adapterDisconnect()
  }, [adapterDisconnect])

  const confirmType = useMemo(
    () => (walletType === 'Ledger' ? SignType.TRANSACTION : SignType.MESSAGE),
    [walletType],
  )

  return {
    connected,
    connecting,
    publicKey: publicKey ? publicKey.toString() : null,
    walletName: wallet?.adapter.name ?? null,
    confirmType,
    connect,
    disconnect,
    signMessage,
    signTransaction,
  }
}
